import { useState, type CSSProperties, type ReactNode } from "react";

import type { PositionType, ReleveRisk } from "../contract/types";
import {
  deviationColors,
  formatDeviation,
  formatDeviationShort,
  formatDuration,
  formatSecondsOfDay,
  positionTypeColorVar,
  positionTypeLabel,
} from "../app/format";
import { useLines } from "../config/LinesProvider";

/** Écart signé, coloré signal au-delà du seuil. */
export function DeviationValue({
  seconds,
  outOfTolerance,
  style,
}: {
  seconds: number;
  outOfTolerance?: boolean;
  style?: CSSProperties;
}) {
  return (
    <span
      className="mono"
      style={{ fontWeight: 700, color: outOfTolerance ? "var(--sentinelle-signal)" : "var(--sentinelle-ink-2)", ...style }}
    >
      {formatDeviation(seconds)}
    </span>
  );
}

/**
 * Badge d'écart au **standard Sentinelle** (§E.3, décision utilisateur 2026-09-07) :
 *  - avance ⇒ `A:MM` fond **rouge** (l'avance est interdite → infraction) ;
 *  - retard ⇒ `R:MM` fond **vert** (le retard est toléré tant qu'il ne dépasse
 *    pas le seuil) ;
 *  - à l'heure / retard < 1 min ⇒ « À l'heure », neutre.
 * Texte blanc. À réutiliser partout (file d'écarts, panneau véhicule, thermomètre…).
 */
export function DeviationBadge({ seconds, style }: { seconds: number; style?: CSSProperties }) {
  const { bg, fg } = deviationColors(seconds);
  return (
    <span
      className="mono"
      style={{
        display: "inline-block",
        fontWeight: 800,
        fontSize: "0.72rem",
        lineHeight: 1.5,
        padding: "0 6px",
        borderRadius: 4,
        whiteSpace: "nowrap",
        color: fg,
        background: bg,
        ...style,
      }}
    >
      {formatDeviationShort(seconds)}
    </span>
  );
}

// Même orange que la surcouche Fréquence de la carte pilote (§5.4-like, « paquet » —
// `FREQUENCY_FILL.bunched` dans `map/VehicleMarkers.tsx`, repris aussi par `MapLegend`).
// Une seule couleur « paquet » dans toute l'app plutôt que d'en inventer une 3ᵉ ici.
// Exportée : `MapDeviationPanel` s'en sert aussi pour son compteur d'en-tête.
export const BUNCHING_BG = "#e0782f";

/**
 * Badge d'un écart `kind: "bunching"` (signalé par Claude-2, v0.217.0) — sur CET
 * écart, `deviationSeconds` porte l'**intervalle entre les deux véhicules**, pas
 * un écart à l'horaire. Ne JAMAIS le passer à `DeviationBadge` : ses couleurs
 * avance/retard et son texte `A:MM`/`R:MM` n'ont aucun sens sur un intervalle —
 * sur une ligne peu fréquente, un intervalle de 8 min s'y lirait « retard de
 * 8 min ». Durée brute (non signée), couleur « paquet » partagée avec la carte.
 * Partagé par `DeviationQueue` et `MapDeviationPanel` : seule la mise en page des
 * deux panneaux diverge, pas ce badge (contrairement à avance/retard — l'un est
 * bleu/rouge, l'autre rouge/vert, décision utilisateur distincte par panneau).
 */
export function BunchingBadge({ intervalSeconds, style }: { intervalSeconds: number; style?: CSSProperties }) {
  return (
    <span
      className="mono"
      style={{
        display: "inline-block",
        fontWeight: 800,
        fontSize: "0.72rem",
        lineHeight: 1.5,
        padding: "0 6px",
        borderRadius: 4,
        whiteSpace: "nowrap",
        color: "#fff",
        background: BUNCHING_BG,
        ...style,
      }}
    >
      {formatDuration(Math.abs(intervalSeconds))}
    </span>
  );
}

// Violet, libre dans l'app (avance/retard/paquet ont déjà chacun leur couleur) —
// la pastille « pause légale » réutilise plutôt le rouge déjà établi partout
// ailleurs pour une alerte sérieuse (`RETARD_BG`, `mae-veh-alert`…), pas une
// 2ᵉ couleur inventée pour le même niveau de gravité.
export const RELEVE_RISK_BG = "#7c3aed";
export const LEGAL_BREAK_RISK_BG = "#dc2626";

/**
 * Pastille « relève menacée » (contrat v1.23, `Deviation.kind: "releve-risk"`
 * / `ReleveRisk`) — demandée par Claude-2 à la livraison du contrat
 * (« pastille rouge si `legalBreakAtRisk` »), jamais câblée côté front avant
 * la demande utilisateur du 2026-10-02. `deviationSeconds` reste un vrai écart
 * à l'horaire sur CET écart (contrairement à `bunching`) ; ce badge est un
 * signal SUPPLÉMENTAIRE, pas un remplacement de `DeviationBadge`.
 *
 * Contenu : la MARGE avant la relève, signée comme un écart (`formatDeviation`)
 * — négatif = déjà dépassée. Rouge + libellé « pause » si `legalBreakAtRisk`
 * (la coupure suivante du plan de service tomberait sous le seuil légal,
 * `thresholds.minLegalBreakSeconds` — valeur **interne au serveur**, jamais
 * transmise au front : impossible d'afficher le seuil en minutes ici sans le
 * deviner, voir `docs/CONTRAT-DEMANDES.md`). Violet + « relève » sinon.
 */
export function ReleveRiskBadge({ releveRisk, style }: { releveRisk: ReleveRisk; style?: CSSProperties }) {
  const { legalBreakAtRisk, marginSeconds, placeName, atSeconds } = releveRisk;
  const title =
    `Relève prévue à ${placeName} vers ${formatSecondsOfDay(atSeconds)}` +
    (legalBreakAtRisk ? " — pause légale menacée par le retard" : "");
  return (
    <span
      className="mono"
      title={title}
      style={{
        display: "inline-block",
        fontWeight: 800,
        fontSize: "0.72rem",
        lineHeight: 1.5,
        padding: "0 6px",
        borderRadius: 4,
        whiteSpace: "nowrap",
        color: "#fff",
        background: legalBreakAtRisk ? LEGAL_BREAK_RISK_BG : RELEVE_RISK_BG,
        ...style,
      }}
    >
      {formatDeviation(marginSeconds)}
    </span>
  );
}

/** Pastille de type de position (CDC §3.1) — encodage visuel + libellé accessible. */
export function PositionDot({ type, withLabel = false }: { type: PositionType; withLabel?: boolean }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }} title={`Position ${positionTypeLabel(type)}`}>
      <span
        aria-hidden
        style={{
          width: 8,
          height: 8,
          borderRadius: type === "gps" ? "50%" : 2,
          background: positionTypeColorVar(type),
          border: type === "stale" ? "1px dashed var(--sentinelle-ink-3)" : "none",
        }}
      />
      {withLabel ? <span className="ovl">{positionTypeLabel(type)}</span> : null}
      <span className="is-sr-only">Position {positionTypeLabel(type)}</span>
    </span>
  );
}

/** Badge de ligne, couleur issue du référentiel serveur. */
/**
 * Pastille de ligne. Utilise en priorité le **logo SVG de marque** (`/line-icons/
 * <ref>.svg`, §G / D-014) ; sur toute erreur de chargement, repli sur la pastille
 * CSS (code ligne sur fond `line.color`).
 */
export function LineBadge({ lineId, size = 24 }: { lineId: string; size?: number }) {
  const lines = useLines();
  const code = lines.code(lineId);
  const [broken, setBroken] = useState(false);

  if (!broken) {
    return (
      <img
        src={lines.iconUrl(lineId)}
        alt={`Ligne ${code}`}
        width={size}
        height={size}
        loading="lazy"
        onError={() => setBroken(true)}
        style={{ display: "inline-block", flex: "none", borderRadius: 3, objectFit: "contain" }}
      />
    );
  }
  return (
    <span
      className="mono"
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: size,
        height: size,
        padding: "0 6px",
        fontSize: "0.72rem",
        fontWeight: 700,
        borderRadius: 2,
        background: lines.color(lineId),
        color: lines.textColor(lineId),
      }}
    >
      {code}
    </span>
  );
}

export function PanelHeading({ children, aside }: { children: ReactNode; aside?: ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, gap: 10 }}>
      <h2
        style={{
          font: "700 12.5px/1 var(--bulma-family-secondary)",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: "var(--sentinelle-ink)",
          margin: 0,
        }}
      >
        {children}
      </h2>
      {aside}
    </div>
  );
}
